import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
    readBrowseState,
    writeBrowseState,
    withFilter,
    withoutResource,
    toApiQuery,
    SORTS
} from '../../services/browseState';

// Η γέφυρα ανάμεσα στην κατάσταση περιήγησης και τη **διεύθυνση**.
//
// Όρος αναζήτησης, φίλτρα, ταξινόμηση, σελίδα και ενεργή καρτέλα ζουν στο URL και
// **όχι** σε τοπική κατάσταση, ώστε κάθε προβολή να είναι κοινοποιήσιμη με
// σύνδεσμο και να επιβιώνει της ανανέωσης (**D27**).
//
// Οι **καθαροί κανόνες** —τι είναι ενεργό φίλτρο, πότε ανοίγει η περιοχή, ποια
// είναι η προεπιλογή— ζουν στο `services/browseState.js` και δοκιμάζονται χωριστά.
// Εδώ μόνο η σύνδεσή τους με τον router.

// Το `tabs` είναι **προαιρετικό**: μόνο η σελίδα συλλογής έχει καρτέλες. Οι
// άλλες δύο οθόνες που καλούν αυτό το hook —κατάλογος συλλογών και αναζήτηση—
// δεν το δίνουν, οπότε το `tab` μένει κενό και δεν γράφεται ποτέ στη διεύθυνσή
// τους. Καμία αλλαγή σε αυτές.

// Ο συγχρονισμός **διεύθυνσης και οθόνης**, μία φορά για τις τρεις οθόνες που
// τον χρειάζονται (κατάλογος συλλογών, σελίδα συλλογής, αναζήτηση).
//
// Η καθαρή λογική ζει στο `services/browseState.js` και δοκιμάζεται εκεί. Εδώ
// μένει μόνο ό,τι είναι πράγματι React: η ανάγνωση από τη διεύθυνση και οι
// αποφάσεις **ιστορικού**, που είναι και το λεπτό σημείο.
//
// Ποια μεταβολή προσθέτει καταχώρηση ιστορικού και ποια αντικαθιστά:
//
//   πληκτρολόγηση όρου  → αντικατάσταση. Αλλιώς κάθε γράμμα είναι μια καταχώρηση
//                          και το «πίσω» χρειάζεται δεκαπέντε πατήματα.
//   σελίδα, ταξινόμηση,
//   φίλτρα              → προσθήκη. Είναι σκόπιμες μεταβάσεις, και ο χρήστης
//                          περιμένει να επιστρέψει στην προηγούμενη σελίδα.
//   άνοιγμα πόρου       → προσθήκη, και το κλείσιμο την **αναιρεί** (D15).

/**
 * @param {object} [options]
 * @param {string[]} [options.sorts] Οι επιτρεπτές ταξινομήσεις της οθόνης.
 * @param {string[]} [options.tabs] Οι καρτέλες της οθόνης, αν έχει (D27).
 */
export function useBrowseState({ sorts = SORTS.resources, tabs = null } = {}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const search = searchParams.toString();

    const state = useMemo(
        () => readBrowseState(search, { sorts, tabs }),
        [search, sorts, tabs]
    );

    /**
     * Αληθές όταν **εμείς** προσθέσαμε την καταχώρηση που άνοιξε τον πόρο.
     *
     * Χρειάζεται γιατί το κλείσιμο έχει δύο σωστές συμπεριφορές. Αν ο χρήστης
     * άνοιξε τον πόρο από την οθόνη, το κλείσιμο πρέπει να **αναιρέσει** την
     * καταχώρηση, ώστε ο κύκλος να μην αφήνει ίχνος. Αν όμως έφτασε από
     * κοινοποιημένο σύνδεσμο, δεν υπάρχει τίποτα να αναιρεθεί: ένα `navigate(-1)`
     * θα τον έβγαζε από την εφαρμογή, στη σελίδα απ' όπου ήρθε.
     */
    const pushedByUs = useRef(false);

    useEffect(() => {
        // Το «πίσω» του περιηγητή έκλεισε το modal μόνο του: η καταχώρηση δεν
        // υπάρχει πλέον, οπότε ούτε το δικαίωμα να την αναιρέσουμε.
        if (!state.resource) pushedByUs.current = false;
    }, [state.resource]);

    const apply = useCallback(
        (next, { replace = false } = {}) => {
            setSearchParams(writeBrowseState(next, { sorts, tabs }), { replace });
        },
        [setSearchParams, sorts, tabs]
    );

    /** Μεταβολή φίλτρου: μηδενίζει τη σελίδα (βλ. `withFilter`). */
    const setFilter = useCallback(
        (changes, options) => apply(withFilter(state, changes), options),
        [apply, state]
    );

    const setPage = useCallback((page) => apply({ ...state, page }), [apply, state]);

    /**
     * Η ενεργή καρτέλα (D27). **Δεν περνά από το `withFilter`**, και είναι το
     * σημείο όπου η προφανής γραμμή θα ήταν λάθος.
     *
     * Το `withFilter` μηδενίζει τη σελίδα, και σωστά — αλλά η σελίδα ανήκει
     * στους **Πόρους** και όχι στην καρτέλα. Περνώντας από εκεί, ο χρήστης που
     * βρίσκεται στη σελίδα 4, κοιτάζει τα καθήκοντα και γυρίζει, θα έβρισκε τη
     * σελίδα 1 — και θα διάβαζε την αλλαγή ως απώλεια της θέσης του.
     *
     * Προσθήκη ιστορικού, όπως η ταξινόμηση και τα φίλτρα: είναι σκόπιμη
     * μετάβαση και το «πίσω» οφείλει να γυρίζει στην προηγούμενη καρτέλα.
     */
    const setTab = useCallback(
        (tab) => {
            if (state.tab === tab) return;
            apply({ ...state, tab });
        },
        [apply, state]
    );

    /** Η ταξινόμηση έρχεται από τη διεπαφή ως `πεδίο:φορά`, σε ένα στοιχείο. */
    const setSort = useCallback(
        (value) => {
            const [sort, order] = String(value).split(':');
            return setFilter({ sort, order: order === 'asc' ? 'asc' : 'desc' });
        },
        [setFilter]
    );

    const openResource = useCallback(
        (resourceId) => {
            if (state.resource === String(resourceId)) return;
            pushedByUs.current = true;
            apply({ ...state, resource: String(resourceId) });
        },
        [apply, state]
    );

    const closeResource = useCallback(() => {
        if (!state.resource) return;

        if (pushedByUs.current) {
            pushedByUs.current = false;
            // Αναίρεση της καταχώρησης που προσθέσαμε: ο κύκλος ανοίγω-κλείνω
            // αφήνει το ιστορικό αμετάβλητο (D15).
            navigate(-1);
            return;
        }

        setSearchParams(withoutResource(search), { replace: true });
    }, [navigate, search, setSearchParams, state.resource]);

    return {
        state,
        query: toApiQuery(state),
        setFilter,
        setPage,
        setSort,
        setTab,
        openResource,
        closeResource
    };
}

/**
 * Το πεδίο αναζήτησης: **άμεσο στην πληκτρολόγηση, καθυστερημένο στο αίτημα**.
 *
 * Χωρίς την καθυστέρηση, κάθε γράμμα είναι ένα αίτημα και μια καταχώρηση
 * ιστορικού. Χωρίς την τοπική κατάσταση, ο κέρσορας πηδά στο τέλος σε κάθε
 * ανανέωση της διεύθυνσης.
 *
 * Η τιμή της διεύθυνσης **υπερισχύει** όταν αλλάξει απ' έξω — κουμπί επιστροφής,
 * κοινοποιημένος σύνδεσμος — ώστε το πεδίο να δείχνει πάντα τι πραγματικά
 * φιλτράρει.
 */
export function useSearchTerm(committed, commit, delay = 300) {
    const [term, setTerm] = useState(committed);
    const latest = useRef(committed);

    useEffect(() => {
        if (committed !== latest.current) {
            latest.current = committed;
            setTerm(committed);
        }
    }, [committed]);

    useEffect(() => {
        if (term === latest.current) return undefined;

        const timer = setTimeout(() => {
            latest.current = term;
            commit(term);
        }, delay);

        return () => clearTimeout(timer);
    }, [term, commit, delay]);

    return [term, setTerm];
}

export default useBrowseState;
